import React, { useCallback, useEffect } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { useScrollspy } from '@sg/garnish';
import {
  CustomizationIngredientsGrid,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
  useCustomizationStepperTimeout,
} from '@sg/shared-fe/features/customization';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useModificationGroupHelperText } from '../../../../hooks';
import { CustomizationModificationGroup } from '../CustomizationModificationGroup';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridPanel = (props: Props) => {
  const { actor, activeTargetId, modificationGroups } = props;

  const { track } = useTelemetry();

  const getSectionHelperText = useModificationGroupHelperText();
  const { activeStepperId, setActiveStepperId, resetStepperTimeout, clearStepperTimeout } = useCustomizationStepperTimeout({ resetOnWindowBlur: true }); // prettier-ignore
  const isModificationGroupHelperTextEnabled = useFeatureFlag('CELS-4762-modification-group-helper-text-enabled'); // prettier-ignore

  const scrollSpy = useScrollspy({
    defaultTargetId: activeTargetId ?? undefined,
  });
  const { scrollViewRef, scrollTo, onScrollViewContentSizeChange, onScrollViewLayout } = scrollSpy;

  const { modificationLimits } = useCustomizationContext({
    actor,
  });

  const { getFormattedQuantity } = useCustomizationHelpers({ actor });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddIngredient = useCallback(() => {
    track('customization.ingredients-tab.ingredient-added');
  }, [track]);

  const onRemoveIngredient = useCallback(() => {
    track('customization.ingredients-tab.ingredient-removed');
  }, [track]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTargetId) return;

    scrollTo(activeTargetId);
  }, [activeTargetId, scrollTo]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView edges={SAFE_AREA_EDGES} style={styles.ingredientsGridContainer}>
      <CustomizationIngredientsGrid.Container
        // @ts-expect-error - AnimatedRef is not assignable to Ref (reanimated v4)
        ref={scrollViewRef}
        onContentSizeChange={onScrollViewContentSizeChange}
        onLayout={onScrollViewLayout}
      >
        {modificationGroups?.map((group) => {
          const freeAggregateQuantity = modificationLimits[group.name]?.freeAggregateQuantity;

          const customHelperText = getSectionHelperText({
            freeAggregateQuantity,
            ingredientKind: group.modificationIngredientKind,
          });
          const helperText = isModificationGroupHelperTextEnabled
            ? group.helperText
            : customHelperText;

          return (
            <CustomizationModificationGroup
              key={group.id}
              actor={actor}
              onAddIngredient={onAddIngredient}
              onRemoveIngredient={onRemoveIngredient}
              helperText={helperText}
              formattedQuantity={getFormattedQuantity(group.name)}
              modificationGroup={group}
              activeStepperId={activeStepperId}
              resetStepperTimeout={resetStepperTimeout}
              clearStepperTimeout={clearStepperTimeout}
              setActiveStepperId={setActiveStepperId}
              scrollSpy={scrollSpy}
            />
          );
        })}
      </CustomizationIngredientsGrid.Container>
    </SafeAreaView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  activeTargetId: string | null | undefined;
  modificationGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
};
